import clsx from "clsx";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { ApiError, describeError } from "../api/http";
import {
  useBiwengerConfig,
  useCheckBiwengerConfig,
  useClearPlan,
  useDiagnostics,
  useSettings,
  useSync,
  useUpdateBiwengerConfig,
  useUpdateSettings,
} from "../api/queries";
import type {
  BiwengerCheck,
  BiwengerConfig,
  BiwengerConfigPatch,
  Settings as Ajustes,
  SettingsPatch,
  ValidationIssue,
} from "../api/types";
import { Page } from "../components/layout/Page";
import { Button } from "../components/ui/Button";
import { ToggleChip } from "../components/ui/Chip";
import { Banner, ErrorState } from "../components/ui/ErrorState";
import { Sheet } from "../components/ui/Sheet";
import { Skeleton } from "../components/ui/Skeleton";
import { useToast } from "../components/ui/Toast";
import { formatDiagnostics } from "../lib/diagnostics";
import { desdeHace } from "../lib/format";
import {
  POSITIONS,
  parseFormation,
  type Formation,
  type Position,
} from "../lib/positions";

/**
 * Pantalla *Ajustes* — M8-T07 (UC-08.07).
 *
 * QUÉ CONFIGURA
 *   Las reglas de la liga (sección 5.2), los parámetros del modelo y del recomendador, y las
 *   horas de la sincronización automática. Todo viaja por `PUT /api/settings` como un parcial
 *   (sección 9.1) y el servidor devuelve los ajustes **ya fusionados**, que es lo que se vuelve a
 *   pintar. `sync.authError` no se toca desde aquí: lo enciende y lo apaga la sincronización.
 *
 * SE LLEGA DESDE *INICIO*, NO DESDE LAS PESTAÑAS
 *   La sección 8.1 fija cinco pestañas y esta es la sexta pantalla, así que lleva botón atrás.
 *
 * DÓNDE VA CADA ERROR (aceptación de la tarea)
 *   El `400` de `PUT /api/settings` trae `details` con la ruta del ajuste que falla
 *   (`league.clauseMarkup`, `sync.cron.market`…). Cada ruta se pinta **junto a su campo**, con
 *   `aria-invalid` y `aria-describedby`, y el foco salta al primero: con veintitantos campos, un
 *   «no válido» global obligaría a buscarlo a ojo.
 *
 * POR QUÉ LOS MOTIVOS SE DICEN EN CASTELLANO
 *   El servidor escribe en español lo suyo (formaciones y expresiones cron), pero los topes
 *   numéricos los redacta `zod` en inglés técnico («Too small: expected number to be >=1»). La
 *   sección 8.3 no admite jerga en la interfaz: el **qué falla** y el **cuál falla** siguen
 *   saliendo del servidor; solo se traduce la frase cuando viene en inglés.
 *
 * LO PELIGROSO, APARTE
 *   *Vaciar datos y resincronizar* vive en su propia tarjeta, lejos de *Guardar ajustes*, y pide
 *   confirmación en un `Sheet` que enumera qué se rehace y qué se conserva.
 */

/* ====================================================================== vocabulario ====== */

/** Las siete formaciones de la sección 5.2: el catálogo que se puede activar o desactivar. */
const FORMACIONES_CONOCIDAS = [
  "3-4-3",
  "3-5-2",
  "4-3-3",
  "4-4-2",
  "4-5-1",
  "5-3-2",
  "5-4-1",
];

/** Un portero y solo uno en cualquier formación (sección 7.1). */
const PORTEROS_POR_FORMACION = 1;

/** Lo que se enseña cuando un campo numérico se queda vacío o con algo que no es un número. */
const FALTA_NUMERO = "Escribe un número.";

/**
 * Las rutas que esta pantalla sabe señalar. Sirven para dos cosas: ordenar el salto de foco al
 * primer campo con error y descubrir un problema **sin campo** —una ruta que no esté en la lista
 * se enseña arriba, en el aviso, en vez de perderse en silencio.
 */
const RUTAS_DE_CAMPO = [
  "league.formations",
  "league.squadMax",
  "league.roundsTotal",
  "league.benchMargin.POR",
  "league.benchMargin.DEF",
  "league.benchMargin.MED",
  "league.benchMargin.DEL",
  "league.maxPerTeam",
  "league.clausesEnabled",
  "league.clauseMarkup",
  "weights.wForm",
  "weights.alpha",
  "weights.beta",
  "weights.gamma",
  "model.formWindow",
  "model.formHalfLife",
  "model.availabilityWindow",
  "recommender.benchWeight",
  "recommender.minDelta",
  "sync.cron.catalog",
  "sync.cron.market",
  "sync.cron.players",
  "sync.cron.rounds",
  "sync.playersDailyQuota",
];

/** Identificador estable de un campo: permite etiquetarlo, describirlo y enfocarlo. */
function campoId(ruta: string, prefijo = "ajustes"): string {
  return `${prefijo}-${ruta.replace(/\./g, "-")}`;
}

/** Las rutas que sabe señalar la tarjeta de conexión, en el orden en que se leen. */
const RUTAS_DE_CONEXION = [
  "mode",
  "token",
  "leagueId",
  "userId",
  "version",
  "competition",
  "scoreId",
  "userAgent",
];

/** Tipo de `<input>` por cada clase de campo. */
const TIPO_DE_ENTRADA = {
  number: "number",
  cron: "text",
  texto: "text",
  identificador: "text",
  secreto: "password",
} as const;

/** Teclado que pide el móvil por cada clase de campo. */
const MODO_DE_ENTRADA = {
  number: "decimal",
  cron: "text",
  texto: "text",
  identificador: "numeric",
  secreto: "text",
} as const;

/** Los que se leen como datos y no como prosa: van en mono y con cifras de ancho fijo. */
const MONOESPACIADOS = new Set(["cron", "identificador", "secreto"]);

/* ================================================================= estado del formulario = */

/**
 * Lo que se edita. Los números que se escriben a mano viven como **texto** —no como número—
 * porque un campo a medio escribir («», «0,», «-») no es ningún número y convertirlo en cada
 * pulsación borraría lo que el usuario está tecleando. Los deslizadores sí son números: no
 * pueden estar a medias.
 */
interface Formulario {
  formations: string[];
  squadMax: string;
  roundsTotal: string;
  benchMargin: Record<Position, string>;
  /** Vacío = sin límite (`null` en la sección 5.2). */
  maxPerTeam: string;
  clausesEnabled: boolean;
  clauseMarkup: string;
  wForm: number;
  alpha: number;
  beta: number;
  gamma: number;
  formWindow: number;
  formHalfLife: number;
  availabilityWindow: number;
  benchWeight: number;
  minDelta: number;
  cron: { catalog: string; market: string; players: string; rounds: string };
  playersDailyQuota: string;
}

function aFormulario(ajustes: Ajustes): Formulario {
  return {
    formations: [...ajustes.league.formations],
    squadMax: String(ajustes.league.squadMax),
    roundsTotal: String(ajustes.league.roundsTotal),
    benchMargin: {
      POR: String(ajustes.league.benchMargin.POR),
      DEF: String(ajustes.league.benchMargin.DEF),
      MED: String(ajustes.league.benchMargin.MED),
      DEL: String(ajustes.league.benchMargin.DEL),
    },
    maxPerTeam:
      ajustes.league.maxPerTeam === null
        ? ""
        : String(ajustes.league.maxPerTeam),
    clausesEnabled: ajustes.league.clausesEnabled,
    clauseMarkup: String(ajustes.league.clauseMarkup),
    wForm: ajustes.weights.wForm,
    alpha: ajustes.weights.alpha,
    beta: ajustes.weights.beta,
    gamma: ajustes.weights.gamma,
    formWindow: ajustes.model.formWindow,
    formHalfLife: ajustes.model.formHalfLife,
    availabilityWindow: ajustes.model.availabilityWindow,
    benchWeight: ajustes.recommender.benchWeight,
    minDelta: ajustes.recommender.minDelta,
    cron: { ...ajustes.sync.cron },
    playersDailyQuota: String(ajustes.sync.playersDailyQuota),
  };
}

/** Lee un número escrito a mano. Admite la coma decimal, que es la que se enseña (sección 8.3). */
function aNumero(texto: string): number | null {
  const limpio = texto.trim().replace(",", ".");
  if (limpio === "") return null;
  const valor = Number(limpio);
  return Number.isFinite(valor) ? valor : null;
}

interface Preparado {
  patch: SettingsPatch;
  /** Lo que ni siquiera hace falta preguntar al servidor: un campo sin número. */
  errores: Record<string, string>;
}

/**
 * Arma el cuerpo de `PUT /api/settings` con **todo** lo editable. Se manda entero y no solo lo
 * cambiado porque el servidor guarda únicamente las claves que difieren de lo que ya había
 * (`settings/store.ts`): mandar de más no escribe de más, y a cambio un guardado enseña de una
 * vez todos los campos que no valen, en vez de uno por intento.
 */
function prepararPatch(formulario: Formulario): Preparado {
  const errores: Record<string, string> = {};

  const numero = (ruta: string, texto: string): number => {
    const valor = aNumero(texto);
    if (valor === null) {
      errores[ruta] = FALTA_NUMERO;
      return 0;
    }
    return valor;
  };

  const patch: SettingsPatch = {
    league: {
      formations: [...formulario.formations],
      squadMax: numero("league.squadMax", formulario.squadMax),
      benchMargin: {
        POR: numero("league.benchMargin.POR", formulario.benchMargin.POR),
        DEF: numero("league.benchMargin.DEF", formulario.benchMargin.DEF),
        MED: numero("league.benchMargin.MED", formulario.benchMargin.MED),
        DEL: numero("league.benchMargin.DEL", formulario.benchMargin.DEL),
      },
      // Un campo vacío es «sin límite», que es `null`. Un 0 significaría «ninguno», que no existe.
      maxPerTeam:
        formulario.maxPerTeam.trim() === ""
          ? null
          : numero("league.maxPerTeam", formulario.maxPerTeam),
      clausesEnabled: formulario.clausesEnabled,
      clauseMarkup: numero("league.clauseMarkup", formulario.clauseMarkup),
      roundsTotal: numero("league.roundsTotal", formulario.roundsTotal),
    },
    weights: {
      wForm: formulario.wForm,
      alpha: formulario.alpha,
      beta: formulario.beta,
      gamma: formulario.gamma,
    },
    model: {
      formWindow: formulario.formWindow,
      formHalfLife: formulario.formHalfLife,
      availabilityWindow: formulario.availabilityWindow,
    },
    recommender: {
      benchWeight: formulario.benchWeight,
      minDelta: formulario.minDelta,
    },
    sync: {
      cron: { ...formulario.cron },
      playersDailyQuota: numero(
        "sync.playersDailyQuota",
        formulario.playersDailyQuota,
      ),
    },
  };

  return { patch, errores };
}

/* ================================================================ errores del servidor === */

function leerIssues(details: unknown): ValidationIssue[] {
  if (!Array.isArray(details)) return [];
  return details.filter(
    (item): item is ValidationIssue =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as ValidationIssue).path === "string" &&
      typeof (item as ValidationIssue).message === "string",
  );
}

/** Cifra de un mensaje del servidor, con la coma decimal de la sección 8.3. */
function conComa(texto: string): string {
  return texto.replace(".", ",");
}

/**
 * El motivo, en castellano. Los mensajes que el servidor ya escribe en español (formaciones,
 * expresiones cron, «Hace falta al menos una formación») se enseñan tal cual, porque son más
 * concretos que cualquier reformulación. Los que `zod` redacta en inglés se traducen: la interfaz
 * va en español y sin jerga técnica (sección 8.3).
 */
function mensajeDeIssue(issue: ValidationIssue): string {
  const mensaje = issue.message;
  if (/^(Formación|Expresión cron|Hace falta)/.test(mensaje)) return mensaje;

  const minimo = /to be >=\s*([\d.]+)/.exec(mensaje);
  if (minimo) return `El valor mínimo es ${conComa(minimo[1])}.`;

  const maximo = /to be <=\s*([\d.]+)/.exec(mensaje);
  if (maximo) return `El valor máximo es ${conComa(maximo[1])}.`;

  if (/expected int/i.test(mensaje)) return "Tiene que ser un número entero.";
  if (/expected number/i.test(mensaje)) return FALTA_NUMERO;
  if (/expected boolean/i.test(mensaje)) return "Solo puede estar sí o no.";
  if (/^Unrecognized key/i.test(mensaje)) return "Ese ajuste no existe.";
  return "Ese valor no vale.";
}

/**
 * De la ruta del problema al campo que la enseña. Un problema dentro de la lista de formaciones
 * llega como `league.formations.2`; el campo es la lista entera, y el mensaje del servidor ya
 * nombra la formación que falla.
 */
/**
 * Los problemas de `PUT /api/biwenger/config`.
 *
 * POR QUÉ NO REUSA `erroresDelServidor`
 *   Los ajustes tienen que traducir lo que `zod` redacta en inglés técnico («Too small: expected
 *   number to be >=1»), y por eso reescribe casi todos los mensajes. La conexión no: sus motivos
 *   los escribe a mano el servidor en castellano y ya dicen qué falla y cómo arreglarlo, así que
 *   pasarlos por aquel traductor los convertiría en un «Ese valor no vale» mucho peor.
 */
function erroresDeConexion(error: ApiError | null): Record<string, string> {
  if (error === null || error.code !== "validation") return {};
  const mapa: Record<string, string> = {};
  for (const issue of leerIssues(error.details)) {
    if (issue.path in mapa) continue;
    // Lo único que puede llegar en inglés: una clave que no existe, que es una errata de quien
    // llama a la API y no algo que un campo del formulario pueda señalar.
    mapa[issue.path] = /^Unrecognized key/i.test(issue.message)
      ? "Ese dato de conexión no existe."
      : issue.message;
  }
  return mapa;
}

function rutaDeCampo(path: string): string {
  if (path.startsWith("league.formations")) return "league.formations";
  return path;
}

function erroresDelServidor(error: ApiError | null): Record<string, string> {
  if (error === null || error.code !== "validation") return {};
  const mapa: Record<string, string> = {};
  for (const issue of leerIssues(error.details)) {
    const ruta = rutaDeCampo(issue.path);
    if (!(ruta in mapa)) mapa[ruta] = mensajeDeIssue(issue);
  }
  return mapa;
}

/* ====================================================================== reglas derivadas = */

/**
 * Mínimos de plantilla por posición (sección 7.1): lo que exige la formación que **menos** pide,
 * más el margen de banquillo. Se calculan aquí para que se vea el efecto de quitar una formación
 * antes de guardar; quien manda sigue siendo el servidor.
 */
function minimosPorPosicion(
  formaciones: readonly string[],
  margen: Record<Position, string>,
): Record<Position, number> | null {
  const leidas = formaciones
    .map((formacion) => parseFormation(formacion))
    .filter((formacion): formacion is Formation => formacion !== null);
  if (leidas.length === 0) return null;

  const extra = (position: Position): number => aNumero(margen[position]) ?? 0;
  const menosExigente = (position: Position): number =>
    Math.min(...leidas.map((formacion) => formacion.counts[position]));

  return {
    POR: PORTEROS_POR_FORMACION + extra("POR"),
    DEF: menosExigente("DEF") + extra("DEF"),
    MED: menosExigente("MED") + extra("MED"),
    DEL: menosExigente("DEL") + extra("DEL"),
  };
}

/* ========================================================================== piezas ======= */

interface TarjetaProps {
  titulo: string;
  hint?: ReactNode;
  peligro?: boolean;
  children: ReactNode;
}

function Tarjeta({ titulo, hint, peligro = false, children }: TarjetaProps) {
  return (
    <section
      className={clsx(
        "grid gap-3 rounded-xl border p-3",
        peligro ? "border-down/45 bg-down/[0.09]" : "border-border bg-surface",
      )}
    >
      <h2
        className={clsx(
          "text-[15px] leading-none",
          peligro ? "text-down" : "text-ink",
        )}
      >
        {titulo}
      </h2>
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
      {children}
    </section>
  );
}

/** La etiqueta en versales del sistema visual, común a todos los campos. */
function Etiqueta({
  htmlFor,
  children,
}: {
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted"
    >
      {children}
    </label>
  );
}

function TextoError({ id, children }: { id: string; children: string }) {
  return (
    <span id={id} role="alert" className="text-xs text-down">
      {children}
    </span>
  );
}

interface CampoProps {
  ruta: string;
  etiqueta: string;
  valor: string;
  onChange: (valor: string) => void;
  error?: string;
  hint?: ReactNode;
  /**
   * Qué se escribe aquí:
   *   · `number`         una cifra de los ajustes (la mayoría).
   *   · `cron`           una expresión horaria: es un dato, va en mono.
   *   · `texto`          texto suelto (la competición, el User-Agent).
   *   · `identificador`  un número que **no** es una cantidad (liga, usuario, baremo): teclado
   *                      numérico en el móvil, pero sin flechas ni rueda, que en un identificador
   *                      solo sirven para cambiarlo sin querer.
   *   · `secreto`        el token: se escribe oculto y el navegador no lo recuerda.
   */
  tipo?: "number" | "cron" | "texto" | "identificador" | "secreto";
  step?: string;
  min?: string;
  placeholder?: string;
  etiquetaPequena?: boolean;
  /** Solo para los campos que no son de los ajustes: cambia el prefijo del identificador. */
  prefijo?: string;
}

/** Un campo de una línea: etiqueta visible, ayuda persistente y el error debajo. */
function Campo({
  ruta,
  etiqueta,
  valor,
  onChange,
  error,
  hint,
  tipo = "number",
  step,
  min,
  placeholder,
  etiquetaPequena = false,
  prefijo = "ajustes",
}: CampoProps) {
  const id = campoId(ruta, prefijo);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const descrito = [hint ? hintId : null, error ? errorId : null]
    .filter((parte): parte is string => parte !== null)
    .join(" ");

  return (
    <div className="grid min-w-0 gap-1.5">
      {etiquetaPequena ? (
        <label
          htmlFor={id}
          className="text-[11px] font-bold tracking-[0.06em] text-muted"
        >
          {etiqueta}
        </label>
      ) : (
        <Etiqueta htmlFor={id}>{etiqueta}</Etiqueta>
      )}
      <input
        id={id}
        type={TIPO_DE_ENTRADA[tipo]}
        inputMode={MODO_DE_ENTRADA[tipo]}
        step={step}
        min={min}
        placeholder={placeholder}
        value={valor}
        onChange={(evento) => onChange(evento.target.value)}
        // Ni el token ni los identificadores de Biwenger son datos que el navegador deba guardar
        // ni ofrecer luego en otro formulario.
        autoComplete={tipo === "number" || tipo === "cron" ? undefined : "off"}
        aria-invalid={error !== undefined}
        aria-describedby={descrito === "" ? undefined : descrito}
        // 16 px de letra: por debajo, iOS hace zoom al enfocar y descuadra la pantalla.
        className={clsx(
          "min-h-touch w-full min-w-0 rounded-[9px] border bg-white/[0.04] px-2.5",
          "text-base text-ink placeholder:text-muted",
          MONOESPACIADOS.has(tipo) && "font-mono tabular-nums",
          error ? "border-down" : "border-border",
        )}
      />
      {hint ? (
        <span id={hintId} className="text-xs text-muted">
          {hint}
        </span>
      ) : null}
      {error ? <TextoError id={errorId}>{error}</TextoError> : null}
    </div>
  );
}

interface DeslizadorProps {
  ruta: string;
  etiqueta: string;
  valor: number;
  min: number;
  max: number;
  step: number;
  formato: (valor: number) => string;
  onChange: (valor: number) => void;
  error?: string;
  hint?: ReactNode;
}

/** Un parámetro acotado: el deslizador no puede dar un valor fuera de rango. */
function Deslizador({
  ruta,
  etiqueta,
  valor,
  min,
  max,
  step,
  formato,
  onChange,
  error,
  hint,
}: DeslizadorProps) {
  const id = campoId(ruta);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const descrito = [hint ? hintId : null, error ? errorId : null]
    .filter((parte): parte is string => parte !== null)
    .join(" ");

  return (
    <div className="grid gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-[13px] text-ink">
          {etiqueta}
        </label>
        <span className="font-mono text-[13px] tabular-nums text-muted">
          {formato(valor)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={valor}
        onChange={(evento) => onChange(Number(evento.target.value))}
        aria-invalid={error !== undefined}
        aria-describedby={descrito === "" ? undefined : descrito}
        className="h-touch w-full accent-pitch"
      />
      {hint ? (
        <span id={hintId} className="text-xs text-muted">
          {hint}
        </span>
      ) : null}
      {error ? <TextoError id={errorId}>{error}</TextoError> : null}
    </div>
  );
}

interface InterruptorProps {
  ruta: string;
  etiqueta: string;
  valor: boolean;
  onChange: (valor: boolean) => void;
  hint?: ReactNode;
  error?: string;
  /** Solo para los campos que no son de los ajustes: cambia el prefijo del identificador. */
  prefijo?: string;
}

function Interruptor({
  ruta,
  etiqueta,
  valor,
  onChange,
  hint,
  error,
  prefijo = "ajustes",
}: InterruptorProps) {
  const id = campoId(ruta, prefijo);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const descrito = [hint ? hintId : null, error ? errorId : null]
    .filter((parte): parte is string => parte !== null)
    .join(" ");

  return (
    <div className="grid gap-1.5">
      <div className="flex min-h-touch items-center justify-between gap-3">
        {/* La etiqueta ocupa la fila entera: así el objetivo táctil son 44 px de alto por todo
            el ancho, y no los 24 px que mide la casilla (sección 8.1). */}
        <label
          htmlFor={id}
          className="flex min-h-touch flex-1 cursor-pointer items-center text-[13px] font-medium text-ink"
        >
          {etiqueta}
        </label>
        <input
          id={id}
          type="checkbox"
          checked={valor}
          onChange={(evento) => onChange(evento.target.checked)}
          aria-describedby={descrito === "" ? undefined : descrito}
          className="h-6 w-11 shrink-0 accent-pitch"
        />
      </div>
      {hint ? (
        <span id={hintId} className="text-xs text-muted">
          {hint}
        </span>
      ) : null}
      {error ? <TextoError id={errorId}>{error}</TextoError> : null}
    </div>
  );
}

/** Factor 0–1 con dos decimales: `0,20`. Los pasos de 0,05 no caben en una sola decimal. */
function factor(valor: number): string {
  return valor.toFixed(2).replace(".", ",");
}

function entero(valor: number): string {
  return String(Math.round(valor));
}

/* ================================================================ conexión con Biwenger = */

/**
 * Lo que se edita de la conexión. Todo es texto: son cabeceras HTTP, no cifras.
 *
 * `token` empieza **siempre vacío** y no se rellena nunca con lo guardado: el servidor no lo
 * devuelve (es lo que justifica que esta tarjeta tenga su propia ruta). Vacío significa «déjalo
 * como está», así que se puede corregir la liga sin volver a pegar el token.
 */
interface FormularioConexion {
  conectar: boolean;
  token: string;
  leagueId: string;
  userId: string;
  version: string;
  competition: string;
  scoreId: string;
  userAgent: string;
}

function aFormularioConexion(config: BiwengerConfig): FormularioConexion {
  return {
    conectar: config.mode === "live",
    token: "",
    leagueId: config.leagueId,
    userId: config.userId,
    version: config.version,
    competition: config.competition,
    scoreId: config.scoreId,
    userAgent: config.userAgent,
  };
}

/** El cambio que se manda: solo lo que de verdad ha cambiado, y el token solo si se ha escrito. */
function prepararConexion(
  formulario: FormularioConexion,
  config: BiwengerConfig,
): BiwengerConfigPatch {
  const patch: BiwengerConfigPatch = {};
  const modo = formulario.conectar ? "live" : "offline";
  if (modo !== config.mode) patch.mode = modo;
  if (formulario.token !== "") patch.token = formulario.token;
  if (formulario.leagueId !== config.leagueId) {
    patch.leagueId = formulario.leagueId;
  }
  if (formulario.userId !== config.userId) patch.userId = formulario.userId;
  if (formulario.version !== config.version) patch.version = formulario.version;
  if (formulario.competition !== config.competition) {
    patch.competition = formulario.competition;
  }
  if (formulario.scoreId !== config.scoreId) patch.scoreId = formulario.scoreId;
  if (formulario.userAgent !== config.userAgent) {
    patch.userAgent = formulario.userAgent;
  }
  return patch;
}

/**
 * Lo que ha dicho *Probar conexión*.
 *
 * POR QUÉ NO ES SIEMPRE UN ERROR ROJO
 *   Que el candado esté echado o que falten datos no es una avería: es el estado normal de una
 *   instalación a medio configurar, y pintarlo en rojo enseñaría a ignorar el rojo. Solo se pinta
 *   así lo que sí es un problema con lo que se acaba de escribir.
 *
 * El motivo lo redacta el servidor —es quien sabe si fue un 401, un 403 o un corte de red— y aquí
 * solo se decide el tono y el titular.
 */
function ResultadoDeLaPrueba({
  resultado,
  error,
  probando,
}: {
  resultado: BiwengerCheck | undefined;
  error: ApiError | null;
  probando: boolean;
}) {
  // Mientras se prueba no se enseña el resultado anterior: sería contestar a la pregunta de antes.
  if (probando) return null;

  if (error !== null) {
    return (
      <Banner tone="error" title="No se ha podido probar la conexión">
        {describeError(error)}
      </Banner>
    );
  }

  if (resultado === undefined) return null;

  if (resultado.ok) {
    const quien = resultado.name === null ? null : `${resultado.name} · `;
    return (
      <Banner tone="success" title="La conexión funciona">
        {quien}
        {resultado.squad === 1
          ? "1 jugador en tu plantilla"
          : `${resultado.squad} jugadores en tu plantilla`}
        . Si no es tu equipo, revisa la liga y el usuario.
      </Banner>
    );
  }

  const leve =
    resultado.reason === "offline" || resultado.reason === "not_configured";
  return (
    <Banner
      tone={leve ? "warning" : "error"}
      title={
        leve
          ? "Todavía no se puede probar"
          : "Biwenger no ha aceptado la conexión"
      }
    >
      {resultado.message}
    </Banner>
  );
}

/**
 * *Conexión con Biwenger* — la tarjeta desde la que se renueva el token sin entrar al servidor
 * (enmienda de la sección 3.2, 28-08-2026).
 *
 * POR QUÉ VA LA PRIMERA Y CON SU PROPIO BOTÓN
 *   Sin conexión no hay datos que ajustar, así que en una instalación nueva es lo primero que hay
 *   que rellenar. Y se guarda aparte porque viaja por otra ruta (`PUT /api/biwenger/config`): un
 *   único botón obligaría a mandar el token cada vez que se toca un peso del modelo.
 *
 * LA ACCIÓN PRIMARIA DE LA PANTALLA SIGUE SIENDO *GUARDAR AJUSTES*
 *   Por eso este botón es secundario aunque esté arriba: dos botones de acento compitiendo se
 *   leen como dos pantallas pegadas.
 *
 * LO AVANZADO, PLEGADO
 *   Competición, baremo y User-Agent traen un valor que sirve para LaLiga y casi nunca se tocan.
 *   Van en un `<details>` nativo —que ya funciona con teclado— para que lo imprescindible quepa
 *   sin desplazarse a 360 px.
 */
function ConexionBiwenger({ authError }: { authError: boolean }) {
  const { showToast } = useToast();
  const consulta = useBiwengerConfig();
  const guardar = useUpdateBiwengerConfig();
  const comprobar = useCheckBiwengerConfig();

  const [formulario, setFormulario] = useState<FormularioConexion | null>(null);
  const config = consulta.data;
  const guardados = useRef<string | null>(null);

  /** Igual que en los ajustes: la respuesta del servidor es la línea base del formulario. */
  useEffect(() => {
    if (config === undefined) return;
    const huella = JSON.stringify(config);
    if (guardados.current === huella) return;
    guardados.current = huella;
    setFormulario(aFormularioConexion(config));
  }, [config]);

  const errores = useMemo(
    () => erroresDeConexion(guardar.error),
    [guardar.error],
  );
  const erroresSinCampo = Object.entries(errores)
    .filter(([ruta]) => !RUTAS_DE_CONEXION.includes(ruta))
    .map(([ruta, mensaje]) => `${ruta}: ${mensaje}`);

  function cambiar(cambio: Partial<FormularioConexion>): void {
    setFormulario((actual) =>
      actual === null ? actual : { ...actual, ...cambio },
    );
    // Un «conexión correcta» junto a un token recién cambiado mentiría: la comprobación vale para
    // lo que había cuando se hizo, no para lo que se esté escribiendo ahora.
    comprobar.reset();
  }

  function probar(): void {
    if (formulario === null || config === undefined || comprobar.isPending) {
      return;
    }
    // Se prueba lo mismo que se guardaría. Con el formulario sin tocar el cambio va vacío, que
    // aquí sí significa algo: comprobar la conexión tal y como está guardada.
    comprobar.mutate(prepararConexion(formulario, config));
  }

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    if (formulario === null || config === undefined || guardar.isPending)
      return;

    const patch = prepararConexion(formulario, config);
    if (Object.keys(patch).length === 0) {
      showToast("No hay nada que guardar");
      return;
    }

    guardar.mutate(patch, {
      onSuccess: () => {
        showToast("Conexión guardada");
      },
      onError: (error) => {
        const mapa = erroresDeConexion(error);
        const ruta = RUTAS_DE_CONEXION.find((candidata) => candidata in mapa);
        if (ruta === undefined) return;
        document.getElementById(campoId(ruta, "conexion"))?.focus();
      },
    });
  }

  if (consulta.isPending || (config !== undefined && formulario === null)) {
    return <Skeleton variant="card" count={1} label="Cargando la conexión" />;
  }

  if (formulario === null || config === undefined) {
    return (
      <Tarjeta titulo="Conexión con Biwenger">
        <Banner tone="error" title="No se ha podido cargar la conexión">
          {describeError(consulta.error)}
        </Banner>
        <Button block onClick={() => void consulta.refetch()}>
          Reintentar
        </Button>
      </Tarjeta>
    );
  }

  const sinGuardar =
    JSON.stringify(formulario) !== JSON.stringify(aFormularioConexion(config));

  return (
    <form onSubmit={enviar} className="grid gap-3">
      <Tarjeta
        titulo="Conexión con Biwenger"
        hint="Cópialos del navegador con Biwenger abierto. El manual explica dónde mirar."
      >
        {authError ? (
          <Banner tone="error" title="Biwenger ha rechazado el token">
            Ha caducado. Cópialo otra vez del navegador, pégalo aquí y guarda:
            el aviso se apaga solo.
          </Banner>
        ) : null}

        {!config.configured ? (
          <Banner tone="warning" title="Todavía no hay conexión">
            Sin el token, la liga, el usuario y la versión no se pueden traer
            datos de Biwenger. Rellénalos aquí abajo.
          </Banner>
        ) : null}

        {guardar.isError && Object.keys(errores).length === 0 ? (
          <Banner tone="error" title="No se ha podido guardar la conexión">
            {describeError(guardar.error)}
          </Banner>
        ) : null}

        {erroresSinCampo.length > 0 ? (
          <Banner tone="error" title="Hay datos que no valen">
            {erroresSinCampo.join(" · ")}
          </Banner>
        ) : null}

        <Campo
          ruta="token"
          prefijo="conexion"
          etiqueta="Token"
          tipo="secreto"
          valor={formulario.token}
          onChange={(valor) => cambiar({ token: valor })}
          error={errores["token"]}
          placeholder={config.tokenSet ? "Guardado" : "Pega aquí el token"}
          hint={
            config.tokenSet
              ? `Guardado, termina en ${config.tokenHint ?? "…"}. Déjalo vacío para no cambiarlo.`
              : "Pega el token entero, sin la palabra Bearer delante."
          }
        />

        <div className="grid grid-cols-2 gap-2.5">
          <Campo
            ruta="leagueId"
            prefijo="conexion"
            etiqueta="Liga"
            tipo="identificador"
            valor={formulario.leagueId}
            onChange={(valor) => cambiar({ leagueId: valor })}
            error={errores["leagueId"]}
            placeholder="000000"
          />
          <Campo
            ruta="userId"
            prefijo="conexion"
            etiqueta="Usuario"
            tipo="identificador"
            valor={formulario.userId}
            onChange={(valor) => cambiar({ userId: valor })}
            error={errores["userId"]}
            placeholder="000000"
          />
        </div>

        <Campo
          ruta="version"
          prefijo="conexion"
          etiqueta="Versión del cliente web"
          tipo="texto"
          valor={formulario.version}
          onChange={(valor) => cambiar({ version: valor })}
          error={errores["version"]}
          placeholder="000"
          hint="Biwenger la manda en cada petición; cámbiala si empieza a rechazarlas."
        />

        <Interruptor
          ruta="mode"
          prefijo="conexion"
          etiqueta="Conectar con Biwenger"
          valor={formulario.conectar}
          onChange={(valor) => cambiar({ conectar: valor })}
          error={errores["mode"]}
          hint="Apagado, la aplicación no hace ni una petición: los datos entran a mano."
        />

        <details className="grid gap-2.5 rounded-[9px] border border-border p-2.5">
          <summary className="min-h-touch cursor-pointer list-none text-[11px] font-bold uppercase tracking-[0.06em] text-muted marker:content-['']">
            Datos avanzados
          </summary>
          <Campo
            ruta="competition"
            prefijo="conexion"
            etiqueta="Competición"
            tipo="texto"
            valor={formulario.competition}
            onChange={(valor) => cambiar({ competition: valor })}
            error={errores["competition"]}
            hint="El nombre que sale en las direcciones de Biwenger. Para LaLiga, la-liga."
          />
          <Campo
            ruta="scoreId"
            prefijo="conexion"
            etiqueta="Baremo de puntos"
            tipo="identificador"
            valor={formulario.scoreId}
            onChange={(valor) => cambiar({ scoreId: valor })}
            error={errores["scoreId"]}
            hint="El sistema de puntuación de tu liga. El 2 es el de SofaScore."
          />
          <Campo
            ruta="userAgent"
            prefijo="conexion"
            etiqueta="Identificación de la aplicación"
            tipo="texto"
            valor={formulario.userAgent}
            onChange={(valor) => cambiar({ userAgent: valor })}
            error={errores["userAgent"]}
            hint="Con lo que esta aplicación se presenta ante Biwenger."
          />
        </details>

        <div className="grid grid-cols-2 gap-2.5">
          {/* Subordinado a propósito: la acción de la tarjeta es guardar; probar solo informa. */}
          <Button
            type="button"
            variant="ghost"
            onClick={probar}
            disabled={comprobar.isPending}
          >
            {comprobar.isPending ? "Probando…" : "Probar conexión"}
          </Button>
          <Button type="submit" disabled={guardar.isPending}>
            {guardar.isPending ? "Guardando…" : "Guardar conexión"}
          </Button>
        </div>

        <ResultadoDeLaPrueba
          resultado={comprobar.data}
          error={comprobar.error}
          probando={comprobar.isPending}
        />

        <p className="text-center text-xs text-muted">
          {sinGuardar ? (
            <span className="text-money">Hay cambios sin guardar.</span>
          ) : config.updatedAt === null ? (
            "Nunca se ha guardado."
          ) : (
            `Guardada ${desdeHace(config.updatedAt)}.`
          )}
        </p>
      </Tarjeta>
    </form>
  );
}

/* ============================================================================ diagnóstico = */

/** Cuántas sincronizaciones se enumeran en pantalla. El informe copiado las lleva todas. */
const RUNS_EN_PANTALLA = 5;

/**
 * *Diagnóstico* — el apartado desde el que se copia lo que ha fallado.
 *
 * PARA QUÉ ESTÁ
 *   Cuando Biwenger cambia una respuesta, arreglarla necesita saber **qué rutas** dejaron de
 *   encajar. Eso vivía solo en el registro del contenedor: había que entrar por SSH al servidor
 *   para leerlo, y el mensaje que sí se guardaba va recortado y nombra cinco rutas como mucho. Con
 *   este botón, el informe entero se pega en una conversación y ya se puede diagnosticar.
 *
 * POR QUÉ EL CUADRO DE TEXTO ADEMÁS DEL BOTÓN
 *   Copiar al portapapeles necesita permiso del navegador y no siempre está disponible. Si falla,
 *   el informe tiene que seguir siendo accesible: se despliega el cuadro, se selecciona solo y se
 *   copia a mano. Vale además para leerlo antes de mandarlo, que con un informe es lo prudente.
 */
function Diagnostico() {
  const { showToast } = useToast();
  const consulta = useDiagnostics();
  const [abierto, setAbierto] = useState(false);
  const cuadro = useRef<HTMLTextAreaElement>(null);

  const informe = consulta.data;
  /**
   * El texto a copiar. Va en `try` porque esta tarjeta es a la que se acude **cuando algo ya está
   * roto**: si un informe con una forma inesperada —un servidor más nuevo que este cliente, por
   * ejemplo— tumbara el formateador, se llevaría por delante la pantalla de *Ajustes* entera y con
   * ella la única salida que quedaba. Antes que eso, el informe en crudo: menos cómodo de leer,
   * pero lleva lo mismo y se puede pegar igual.
   */
  const texto = useMemo(() => {
    if (informe === undefined) return "";
    try {
      return formatDiagnostics(informe);
    } catch {
      return JSON.stringify(informe, null, 2);
    }
  }, [informe]);

  /** Enseña el informe y lo deja seleccionado: es la salida cuando el portapapeles no responde. */
  function ofrecerACopiarAMano(): void {
    setAbierto(true);
    // En el render siguiente, cuando el cuadro ya existe.
    window.setTimeout(() => {
      cuadro.current?.focus();
      cuadro.current?.select();
    }, 0);
  }

  function copiar(): void {
    if (texto === "") return;
    const portapapeles = navigator.clipboard;
    if (portapapeles === undefined) {
      showToast("Copia el informe a mano desde el cuadro de abajo");
      ofrecerACopiarAMano();
      return;
    }
    portapapeles.writeText(texto).then(
      () => {
        showToast("Informe copiado");
      },
      () => {
        showToast("No se ha podido copiar: hazlo a mano desde el cuadro");
        ofrecerACopiarAMano();
      },
    );
  }

  if (consulta.isPending) {
    return (
      <Skeleton variant="card" count={1} label="Cargando el diagnóstico" />
    );
  }

  if (informe === undefined) {
    return (
      <Tarjeta titulo="Diagnóstico">
        <Banner tone="error" title="No se ha podido preparar el informe">
          {describeError(consulta.error)}
        </Banner>
        <Button block onClick={() => void consulta.refetch()}>
          Reintentar
        </Button>
      </Tarjeta>
    );
  }

  // Por lo mismo que el texto: la lista se pinta con lo que haya, no con lo que debería haber.
  const runs = Array.isArray(informe.runs) ? informe.runs : [];
  const fallidas = runs.filter((run) => run.status === "error");
  const enPantalla = runs.slice(0, RUNS_EN_PANTALLA);

  return (
    <Tarjeta
      titulo="Diagnóstico"
      hint="Si una sincronización falla, copia el informe y pégalo donde vayas a pedir ayuda."
    >
      {fallidas.length > 0 ? (
        <Banner
          tone="error"
          title={
            fallidas.length === 1
              ? "La última sincronización ha fallado"
              : `Han fallado ${String(fallidas.length)} sincronizaciones`
          }
        >
          {fallidas[0]?.message ?? "Sin mensaje."}
        </Banner>
      ) : (
        <p className="text-[13px] text-muted">
          Sin fallos en las últimas{" "}
          {runs.length === 0
            ? "sincronizaciones (todavía no ha habido ninguna)"
            : `${String(runs.length)} sincronizaciones`}
          .
        </p>
      )}

      {enPantalla.length > 0 ? (
        <ul className="grid gap-1.5">
          {enPantalla.map((run) => (
            <li
              key={run.id}
              className="grid gap-0.5 border-b border-border pb-1.5 last:border-0 last:pb-0"
            >
              <span className="flex items-baseline justify-between gap-2 text-[13px]">
                <span className="font-mono tabular-nums text-ink">
                  #{run.id} {run.kind}
                </span>
                <span
                  className={clsx(
                    "text-xs",
                    run.status === "error" ? "text-down" : "text-muted",
                  )}
                >
                  {run.status === "error" ? "Error" : "Correcta"}
                  {" · "}
                  {desdeHace(run.startedAt)}
                </span>
              </span>
              {run.detail?.kind === "parse" ? (
                <span className="text-xs text-muted">
                  {run.detail.issues.length} campo
                  {run.detail.issues.length === 1 ? "" : "s"} de «
                  {run.detail.resource}» ya no encaja
                  {run.detail.issues.length === 1 ? "" : "n"} con el esquema
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <Button block onClick={copiar}>
        Copiar informe
      </Button>

      <details
        open={abierto}
        onToggle={(evento) => setAbierto(evento.currentTarget.open)}
        className="grid gap-2.5"
      >
        <summary className="min-h-touch cursor-pointer list-none text-[11px] font-bold uppercase tracking-[0.06em] text-muted marker:content-['']">
          Ver el informe
        </summary>
        <textarea
          ref={cuadro}
          readOnly
          value={texto}
          rows={12}
          aria-label="Informe de diagnóstico"
          className={clsx(
            "w-full min-w-0 rounded-[9px] border border-border bg-white/[0.04] p-2.5",
            "font-mono text-xs leading-relaxed text-ink",
          )}
        />
        <span className="text-xs text-muted">
          No lleva tu token ni los identificadores de tu liga y tu usuario.
        </span>
      </details>
    </Tarjeta>
  );
}

/* ========================================================================= pantalla ====== */

export function Settings() {
  const { showToast } = useToast();
  const consulta = useSettings();
  const actualizar = useUpdateSettings();
  const vaciarPlan = useClearPlan();
  const { sync, isRunning, isStarting } = useSync();

  const [formulario, setFormulario] = useState<Formulario | null>(null);
  const [erroresLocales, setErroresLocales] = useState<Record<string, string>>(
    {},
  );
  const [confirmando, setConfirmando] = useState(false);
  const guardados = useRef<string | null>(null);

  const datos = consulta.data;

  /**
   * Los ajustes que llegan del servidor son la línea base del formulario. Se comparan por
   * contenido y no por identidad: un refresco que devuelve lo mismo no puede borrar lo que se
   * esté escribiendo, y un guardado —que devuelve los ajustes ya fusionados— sí lo actualiza.
   */
  useEffect(() => {
    if (datos === undefined) return;
    const huella = JSON.stringify(datos);
    if (guardados.current === huella) return;
    guardados.current = huella;
    setFormulario(aFormulario(datos));
    setErroresLocales({});
  }, [datos]);

  /**
   * Las formaciones que se pueden marcar: las siete de la sección 5.2 y, detrás, cualquiera que
   * ya estuviera guardada y no sea de esa lista —si no apareciera como chip, quitarla sería un
   * viaje sin vuelta.
   */
  const catalogo = useMemo(() => {
    const extras = (datos?.league.formations ?? []).filter(
      (formacion) => !FORMACIONES_CONOCIDAS.includes(formacion),
    );
    return [...FORMACIONES_CONOCIDAS, ...extras];
  }, [datos]);

  const erroresServidor = useMemo(
    () => erroresDelServidor(actualizar.error),
    [actualizar.error],
  );
  const errores: Record<string, string> = {
    ...erroresServidor,
    ...erroresLocales,
  };

  /** Un problema cuya ruta no tiene campo en esta pantalla: se dice arriba o no se dice. */
  const erroresSinCampo = Object.entries(errores)
    .filter(([ruta]) => !RUTAS_DE_CAMPO.includes(ruta))
    .map(([ruta, mensaje]) => `${ruta}: ${mensaje}`);

  const sinGuardar =
    formulario !== null &&
    datos !== undefined &&
    JSON.stringify(formulario) !== JSON.stringify(aFormulario(datos));

  function cambiar(cambio: Partial<Formulario>): void {
    setFormulario((actual) =>
      actual === null ? actual : { ...actual, ...cambio },
    );
  }

  /** Lleva el foco al primer campo con error, en el orden en el que se leen. */
  function enfocarPrimerError(mapa: Record<string, string>): void {
    const ruta = RUTAS_DE_CAMPO.find((candidata) => candidata in mapa);
    if (ruta === undefined) return;
    document.getElementById(campoId(ruta))?.focus();
  }

  function alternarFormacion(formacion: string): void {
    if (formulario === null) return;
    const activa = formulario.formations.includes(formacion);
    const siguientes = activa
      ? formulario.formations.filter((otra) => otra !== formacion)
      : [...formulario.formations, formacion];
    // Se guardan en el orden del catálogo, no en el de pulsación: la lista es un conjunto.
    cambiar({
      formations: catalogo.filter((otra) => siguientes.includes(otra)),
    });
  }

  function guardar(evento?: FormEvent<HTMLFormElement>): void {
    evento?.preventDefault();
    if (formulario === null || datos === undefined || actualizar.isPending) {
      return;
    }

    const { patch, errores: locales } = prepararPatch(formulario);
    setErroresLocales(locales);
    if (Object.keys(locales).length > 0) {
      // No se pregunta al servidor por algo que ya se sabe: el error anterior sobra.
      actualizar.reset();
      enfocarPrimerError(locales);
      return;
    }

    // La sección 6 manda recalcular las métricas al tocar `weights.*` o `model.*`: lo hace el
    // servidor al guardar, y el aviso lo cuenta para que se entienda por qué cambian las cifras.
    const modeloTocado =
      JSON.stringify(patch.weights) !== JSON.stringify(datos.weights) ||
      JSON.stringify(patch.model) !== JSON.stringify(datos.model);

    actualizar.mutate(patch, {
      onSuccess: () => {
        setErroresLocales({});
        showToast(
          modeloTocado
            ? "Ajustes guardados y métricas recalculadas"
            : "Ajustes guardados",
        );
      },
      onError: (error) => {
        enfocarPrimerError(erroresDelServidor(error));
      },
    });
  }

  function confirmarResincronizacion(): void {
    setConfirmando(false);
    sync("all");
    showToast("Sincronización en marcha");
  }

  const minimos =
    formulario === null
      ? null
      : minimosPorPosicion(formulario.formations, formulario.benchMargin);

  /**
   * Sigue cargando mientras el formulario no exista: los ajustes llegan en un render y el
   * formulario se arma en el efecto siguiente, y sin esto ese hueco enseñaría el error de carga.
   */
  const cargando =
    consulta.isPending || (datos !== undefined && formulario === null);

  const botonGuardar = (
    <Button
      variant="primary"
      size="sm"
      onClick={() => guardar()}
      disabled={formulario === null || actualizar.isPending}
    >
      {actualizar.isPending ? "Guardando…" : "Guardar ajustes"}
    </Button>
  );

  return (
    <Page title="Ajustes" back action={botonGuardar}>
      {cargando ? (
        <Skeleton variant="card" count={3} label="Cargando los ajustes" />
      ) : formulario === null ? (
        <ErrorState
          title="No se han podido cargar los ajustes"
          message={describeError(consulta.error)}
          onRetry={() => void consulta.refetch()}
        />
      ) : (
        <>
          {actualizar.isError && Object.keys(erroresServidor).length === 0 ? (
            <Banner tone="error" title="No se han podido guardar los ajustes">
              {describeError(actualizar.error)}
            </Banner>
          ) : null}

          {erroresSinCampo.length > 0 ? (
            <Banner tone="error" title="Hay ajustes que no valen">
              {erroresSinCampo.join(" · ")}
            </Banner>
          ) : null}

          {/* Va la primera: sin conexión no hay datos que ajustar, y en una instalación nueva es
              lo único que hay que rellenar para que la aplicación empiece a servir de algo. */}
          <ConexionBiwenger authError={datos?.sync.authError ?? false} />

          <form onSubmit={guardar} className="grid gap-3">
            <Tarjeta
              titulo="Reglas de la liga"
              hint="Cópialas de la configuración de tu liga en Biwenger. El manual dice dónde verlas."
            >
              <fieldset className="grid min-w-0 gap-1.5">
                <legend className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
                  Formaciones permitidas
                </legend>
                <div className="flex flex-wrap gap-1.5">
                  {catalogo.map((formacion, indice) => (
                    <ToggleChip
                      key={formacion}
                      id={
                        indice === 0 ? campoId("league.formations") : undefined
                      }
                      pressed={formulario.formations.includes(formacion)}
                      onClick={() => alternarFormacion(formacion)}
                      className="font-mono tabular-nums"
                    >
                      {formacion}
                    </ToggleChip>
                  ))}
                </div>
                <span className="text-xs text-muted">
                  {minimos === null
                    ? "Sin formaciones no se pueden derivar los mínimos de plantilla."
                    : `Mínimos derivados: ${POSITIONS.map(
                        (position) => `${position} ${minimos[position]}`,
                      ).join(" · ")}`}
                </span>
                {errores["league.formations"] ? (
                  <TextoError id={`${campoId("league.formations")}-error`}>
                    {errores["league.formations"]}
                  </TextoError>
                ) : null}
              </fieldset>

              <div className="grid grid-cols-2 gap-2.5">
                <Campo
                  ruta="league.squadMax"
                  etiqueta="Tamaño de plantilla"
                  valor={formulario.squadMax}
                  onChange={(valor) => cambiar({ squadMax: valor })}
                  error={errores["league.squadMax"]}
                  step="1"
                  min="11"
                />
                <Campo
                  ruta="league.roundsTotal"
                  etiqueta="Jornadas totales"
                  valor={formulario.roundsTotal}
                  onChange={(valor) => cambiar({ roundsTotal: valor })}
                  error={errores["league.roundsTotal"]}
                  step="1"
                  min="1"
                />
              </div>

              <fieldset className="grid min-w-0 gap-1.5">
                <legend className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
                  Banquillo extra por posición
                </legend>
                <div className="grid grid-cols-4 gap-2">
                  {POSITIONS.map((position) => (
                    <Campo
                      key={position}
                      ruta={`league.benchMargin.${position}`}
                      etiqueta={position}
                      etiquetaPequena
                      valor={formulario.benchMargin[position]}
                      onChange={(valor) =>
                        cambiar({
                          benchMargin: {
                            ...formulario.benchMargin,
                            [position]: valor,
                          },
                        })
                      }
                      error={errores[`league.benchMargin.${position}`]}
                      step="1"
                      min="0"
                    />
                  ))}
                </div>
              </fieldset>

              <Campo
                ruta="league.maxPerTeam"
                etiqueta="Máximo por equipo"
                valor={formulario.maxPerTeam}
                onChange={(valor) => cambiar({ maxPerTeam: valor })}
                error={errores["league.maxPerTeam"]}
                hint="Déjalo vacío si tu liga no lo limita."
                placeholder="Sin límite"
                step="1"
                min="1"
              />

              <Interruptor
                ruta="league.clausesEnabled"
                etiqueta="Cláusulas activas"
                valor={formulario.clausesEnabled}
                onChange={(valor) => cambiar({ clausesEnabled: valor })}
                error={errores["league.clausesEnabled"]}
                hint="Con cláusulas, el recomendador también mira jugadores de otros equipos."
              />

              <Campo
                ruta="league.clauseMarkup"
                etiqueta="Multiplicador de cláusula"
                valor={formulario.clauseMarkup}
                onChange={(valor) => cambiar({ clauseMarkup: valor })}
                error={errores["league.clauseMarkup"]}
                hint="Lo que se paga sobre el precio cuando Biwenger no da la cláusula."
                step="0.1"
                min="1"
              />
            </Tarjeta>

            <Tarjeta
              titulo="Modelo"
              hint="Cambiar estos parámetros recalcula las métricas: Plantilla y Mercado cambian al guardar."
            >
              <Deslizador
                ruta="weights.wForm"
                etiqueta="Peso de la forma"
                valor={formulario.wForm}
                min={0}
                max={1}
                step={0.05}
                formato={factor}
                onChange={(valor) => cambiar({ wForm: valor })}
                error={errores["weights.wForm"]}
                hint="0 es solo la media de temporada; 1, solo las últimas jornadas."
              />
              <Deslizador
                ruta="weights.alpha"
                etiqueta="Peso de los puntos esperados"
                valor={formulario.alpha}
                min={0}
                max={1}
                step={0.05}
                formato={factor}
                onChange={(valor) => cambiar({ alpha: valor })}
                error={errores["weights.alpha"]}
              />
              <Deslizador
                ruta="weights.beta"
                etiqueta="Peso de la eficiencia por euro"
                valor={formulario.beta}
                min={0}
                max={1}
                step={0.05}
                formato={factor}
                onChange={(valor) => cambiar({ beta: valor })}
                error={errores["weights.beta"]}
              />
              <Deslizador
                ruta="weights.gamma"
                etiqueta="Peso de la tendencia de valor"
                valor={formulario.gamma}
                min={0}
                max={1}
                step={0.05}
                formato={factor}
                onChange={(valor) => cambiar({ gamma: valor })}
                error={errores["weights.gamma"]}
              />
              <Deslizador
                ruta="model.formWindow"
                etiqueta="Ventana de forma"
                valor={formulario.formWindow}
                min={3}
                max={15}
                step={1}
                formato={entero}
                onChange={(valor) => cambiar({ formWindow: valor })}
                error={errores["model.formWindow"]}
                hint="Jornadas que se miran para medir la forma."
              />
              <Deslizador
                ruta="model.formHalfLife"
                etiqueta="Semivida de la forma"
                valor={formulario.formHalfLife}
                min={1}
                max={8}
                step={1}
                formato={entero}
                onChange={(valor) => cambiar({ formHalfLife: valor })}
                error={errores["model.formHalfLife"]}
                hint="Cada cuántas jornadas pesa la mitad lo que pasó antes."
              />
              <Deslizador
                ruta="model.availabilityWindow"
                etiqueta="Ventana de disponibilidad"
                valor={formulario.availabilityWindow}
                min={3}
                max={15}
                step={1}
                formato={entero}
                onChange={(valor) => cambiar({ availabilityWindow: valor })}
                error={errores["model.availabilityWindow"]}
              />
              <Deslizador
                ruta="recommender.benchWeight"
                etiqueta="Peso del banquillo"
                valor={formulario.benchWeight}
                min={0}
                max={1}
                step={0.05}
                formato={factor}
                onChange={(valor) => cambiar({ benchWeight: valor })}
                error={errores["recommender.benchWeight"]}
              />
              <Deslizador
                ruta="recommender.minDelta"
                etiqueta="Mejora mínima por movimiento"
                valor={formulario.minDelta}
                min={0}
                max={1}
                step={0.05}
                formato={factor}
                onChange={(valor) => cambiar({ minDelta: valor })}
                error={errores["recommender.minDelta"]}
                hint="Puntos por jornada que tiene que ganar un cambio para proponerse."
              />
            </Tarjeta>

            <Tarjeta
              titulo="Sincronización automática"
              hint="Cinco campos: minuto, hora, día del mes, mes y día de la semana. Zona horaria Europe/Madrid."
            >
              <Campo
                ruta="sync.cron.catalog"
                etiqueta="Catálogo"
                tipo="cron"
                valor={formulario.cron.catalog}
                onChange={(valor) =>
                  cambiar({ cron: { ...formulario.cron, catalog: valor } })
                }
                error={errores["sync.cron.catalog"]}
              />
              <Campo
                ruta="sync.cron.market"
                etiqueta="Mercado"
                tipo="cron"
                valor={formulario.cron.market}
                onChange={(valor) =>
                  cambiar({ cron: { ...formulario.cron, market: valor } })
                }
                error={errores["sync.cron.market"]}
              />
              <Campo
                ruta="sync.cron.players"
                etiqueta="Fichas"
                tipo="cron"
                valor={formulario.cron.players}
                onChange={(valor) =>
                  cambiar({ cron: { ...formulario.cron, players: valor } })
                }
                error={errores["sync.cron.players"]}
              />
              <Campo
                ruta="sync.cron.rounds"
                etiqueta="Jornadas"
                tipo="cron"
                valor={formulario.cron.rounds}
                onChange={(valor) =>
                  cambiar({ cron: { ...formulario.cron, rounds: valor } })
                }
                error={errores["sync.cron.rounds"]}
              />
              <Campo
                ruta="sync.playersDailyQuota"
                etiqueta="Cuota diaria de fichas"
                valor={formulario.playersDailyQuota}
                onChange={(valor) => cambiar({ playersDailyQuota: valor })}
                error={errores["sync.playersDailyQuota"]}
                hint="Fichas que se traen cada día, a una petición por segundo."
                step="1"
                min="0"
              />
            </Tarjeta>

            {sinGuardar ? (
              <p className="text-center text-xs text-money">
                Hay cambios sin guardar.
              </p>
            ) : null}
          </form>

          <Tarjeta
            titulo="Plan"
            hint="Quita de una vez todas las compras y las ventas previstas."
          >
            <Button
              block
              onClick={() =>
                vaciarPlan.mutate(undefined, {
                  onSuccess: () => showToast("Plan vaciado"),
                })
              }
              disabled={vaciarPlan.isPending}
            >
              Vaciar plan
            </Button>
            {vaciarPlan.isError ? (
              <TextoError id="ajustes-plan-error">
                {describeError(vaciarPlan.error)}
              </TextoError>
            ) : null}
          </Tarjeta>

          {/* Entre el plan y la zona de peligro: es una herramienta de avería, así que se busca
              cuando algo va mal, pero no compite con lo que se viene a configurar. */}
          <Diagnostico />

          <Tarjeta titulo="Zona de peligro" peligro>
            <p className="text-[13px] text-ink">
              Vuelve a traer de Biwenger equipos, jugadores, jornadas y mercado,
              y recalcula las métricas. Lo que haya ahora se sustituye por lo
              que llegue. Tus ajustes y tu plan se conservan.
            </p>
            <Button
              variant="danger"
              block
              onClick={() => setConfirmando(true)}
              disabled={isRunning || isStarting}
            >
              Vaciar datos y resincronizar
            </Button>
            {isRunning ? (
              <span className="text-xs text-muted">
                Ya hay una sincronización en marcha.
              </span>
            ) : null}
          </Tarjeta>

          <p className="text-center text-xs text-muted">
            El manual de configuración explica de dónde sacar cada dato:{" "}
            <span className="font-mono">docs/CONFIGURACION.md</span>, en la
            carpeta del proyecto.
          </p>

          <Sheet
            open={confirmando}
            onClose={() => setConfirmando(false)}
            title="¿Vaciar datos y resincronizar?"
            footer={
              <>
                <Button onClick={() => setConfirmando(false)}>Cancelar</Button>
                <Button variant="danger" onClick={confirmarResincronizacion}>
                  Vaciar y resincronizar
                </Button>
              </>
            }
          >
            <p className="text-[13px] text-muted">
              Se vuelven a traer equipos, jugadores, jornadas y mercado, y se
              recalculan las métricas con lo que llegue.{" "}
              <strong className="text-ink">
                Tus ajustes y tu plan se conservan.
              </strong>{" "}
              La primera pasada de fichas puede tardar varios minutos.
            </p>
          </Sheet>
        </>
      )}
    </Page>
  );
}
